import { z } from 'zod';
import { compare, isAnswered, OPERATORS } from './conditions.js';
import { canonicalJson } from './json.js';
import { displayStates, EFFECT_TYPES, Provenance, withImplied, } from './questions.js';
/**
 * The rules engine: deterministic IF/THEN rules over an assessment's answers and the facts known about the
 * organisation. Pure functions, no I/O: the same rules, answers, facts and date always give the same
 * outcome, so the core can store it, the studio can dry-run a draft against a real assessment, and a
 * re-score can be compared before anyone approves it.
 *
 *   {
 *     name: 'Backup recovery unverified',
 *     when: { any: [ { question: 'Q283', op: 'eq', value: 'never' }, ... ] },
 *     then: [ { type: 'create_finding', params: { code: 'BACKUP_RECOVERY_UNVERIFIED', control: 'BR-03' } } ],
 *   }
 *
 * One pass: every enabled rule is evaluated against the same snapshot, and no rule's effects change what
 * another rule sees. Rules only decide; the modules that own findings, tasks, evidence requests and scores
 * act on the effects.
 */
export const RULES_ENGINE = 'rules/v1';
/**
 * Rule conditions use the question engine's operators plus:
 * - `expired`, `older_than`, `within_last`, `within_next` for dates, against the evaluation date (`as_of`);
 * - `verified`: answered from a verifying source (a checked document, an integration, a scan);
 * - a `provenance` list on a question test, which counts the answer only when it came from one of them.
 */
export const RULE_OPERATORS = [
    ...OPERATORS,
    'expired',
    'older_than',
    'within_last',
    'within_next',
    'verified',
];
export const RuleOperator = z.enum(RULE_OPERATORS);
const NO_VALUE = new Set(['answered', 'not_answered', 'expired', 'verified']);
const DURATION_OPS = new Set(['older_than', 'within_last', 'within_next']);
/** Operators that compare numbers or dates; a choice question can't be tested with them. */
export const ORDERED_RULE_OPERATORS = new Set([
    'gt', 'gte', 'lt', 'lte', 'between', 'expired', 'older_than', 'within_last', 'within_next',
]); // prettier-ignore
/** Sources that check a fact rather than take someone's word for it. */
export const VERIFIED_PROVENANCE = new Set([
    'DOCUMENT_VERIFIED',
    'API_DISCOVERED',
    'SECURITY_SCAN',
    'EXTERNAL_PROVIDER',
]);
export const Duration = z.string().regex(/^\d{1,4}[dwmy]$/, 'a duration such as 90d, 6w, 6m or 1y');
const Scalar = z.union([z.string(), z.number(), z.boolean()]);
const Value = z.union([Scalar, z.array(Scalar)]).optional();
function checkValue(c, ctx) {
    const at = (message) => ctx.addIssue({ code: 'custom', message, path: ['value'] });
    if (NO_VALUE.has(c.op))
        return c.value !== undefined ? at(`${c.op} takes no value`) : undefined;
    if (c.value === undefined)
        return at(`${c.op} needs a value`);
    if ((c.op === 'in' || c.op === 'not_in') && !Array.isArray(c.value))
        at(`${c.op} needs a list`);
    if (c.op === 'between' && !(Array.isArray(c.value) && c.value.length === 2))
        at('between needs [low, high]');
    if (DURATION_OPS.has(c.op) && !Duration.safeParse(c.value).success)
        at(`${c.op} needs a duration such as 90d, 6w, 6m or 1y`);
}
const QuestionRuleTest = z
    .strictObject({
    question: z.string().min(1),
    op: RuleOperator,
    value: Value,
    provenance: z.array(Provenance).min(1).optional(),
})
    .superRefine(checkValue);
const FactRuleTest = z
    .strictObject({
    /** A fact about the organisation, e.g. `industry`, or a dotted path such as `twin.operates_ot`. */
    fact: z.string().min(1),
    op: RuleOperator,
    value: Value,
})
    .superRefine((c, ctx) => {
    checkValue(c, ctx);
    if (c.op === 'verified')
        ctx.addIssue({ code: 'custom', message: 'facts carry no provenance; test a question', path: ['op'] });
});
export const RuleCondition = z.lazy(() => z.union([
    QuestionRuleTest,
    FactRuleTest,
    z.strictObject({ all: z.array(RuleCondition).min(1) }),
    z.strictObject({ any: z.array(RuleCondition).min(1) }),
    z.strictObject({ not: RuleCondition }),
]));
const Id = z.string().min(1).max(100);
/**
 * What each effect needs, so a rule can't be published with an effect its executor can't act on. Extra
 * parameters are allowed and passed through.
 */
export const EFFECT_PARAMS = {
    score_change: z.looseObject({ control: Id, implementation: z.number().min(0).max(1).nullable() }),
    create_risk: z.looseObject({ risk: Id }),
    create_finding: z
        .looseObject({
        code: Id.optional(),
        control: Id.optional(),
        kill_switch: z.boolean().optional(),
        severity: z.enum(['low', 'medium', 'high', 'critical']).optional(),
    })
        .refine((p) => p.code || p.control, 'a finding needs a code or a control'),
    ask_question: z.looseObject({ question: Id }),
    skip_questions: z.looseObject({ questions: z.array(Id).min(1) }),
    start_workflow: z.looseObject({ workflow: Id }),
    complete_workflow: z.looseObject({ workflow: Id }),
    request_evidence: z
        .looseObject({ control: Id.optional(), evidence: z.string().optional(), document_type: Id.optional() })
        .refine((p) => p.control || p.document_type, 'say which control or document type'),
    create_graph_entity: z.looseObject({ entity: Id }),
    create_relationship: z.looseObject({ relationship: Id }),
    create_task: z.looseObject({ task: Id }),
    create_reminder: z.looseObject({
        offsets: z
            .array(z.string().regex(/^T-\d{1,4}$/, 'T-<days>, e.g. T-120'))
            .min(1)
            .max(10)
            .optional(),
        title: z.string().max(200).optional(),
        kind: z.enum(['renewal', 'expiry', 'review']).optional(),
    }),
    send_notification: z.looseObject({ to: Id }),
    require_human_review: z.looseObject({ reason: z.string().max(500).optional() }),
    invoke_ai: z.looseObject({ agent: Id }),
    call_external_api: z.looseObject({ connector: Id }),
};
export const RuleEffect = z
    .strictObject({
    type: z.enum(EFFECT_TYPES),
    params: z.record(z.string(), z.unknown()).default({}),
})
    .superRefine((e, ctx) => {
    const r = EFFECT_PARAMS[e.type].safeParse(e.params);
    if (!r.success)
        r.error.issues.forEach((i) => ctx.addIssue({ code: 'custom', message: i.message, path: ['params', ...i.path] }));
});
export const RuleDefinition = z.strictObject({
    name: z.string().trim().min(1).max(200),
    description: z.string().max(1000).default(''),
    /** A disabled rule is published but never fires. */
    enabled: z.boolean().default(true),
    when: RuleCondition,
    /** In the order they are executed. */
    then: z.array(RuleEffect).min(1).max(20),
});
/** Every question a condition reads. */
export function ruleQuestionRefs(c, out = new Set()) {
    for (const t of ruleQuestionTests(c))
        out.add(t.question);
    return out;
}
/** Every leaf test on a question, for checking values against the question's options. */
export function ruleQuestionTests(c, out = []) {
    if ('all' in c)
        c.all.forEach((x) => ruleQuestionTests(x, out));
    else if ('any' in c)
        c.any.forEach((x) => ruleQuestionTests(x, out));
    else if ('not' in c)
        ruleQuestionTests(c.not, out);
    else if ('question' in c)
        out.push(c);
    return out;
}
const byKey = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
export function evaluateRules(given) {
    const values = {};
    for (const [k, a] of Object.entries(given.answers))
        values[k] = a.value;
    const states = displayStates(given.questions, { answers: values, facts: given.facts });
    // Questions a route counts as not in place (task 85) read as that answer, inferred.
    const inp = { ...given, answers: withImplied(given.answers, states, (value) => ({ value, provenance: 'INFERRED' })) }; // prettier-ignore
    const counts = (k) => k in inp.questions && states[k]?.state !== 'hidden' && isAnswered(inp.answers[k]?.value);
    const effects = [];
    const ignored = Object.keys(given.answers).filter((k) => states[k]?.state === 'implied' && isAnswered(given.answers[k].value));
    for (const k of Object.keys(inp.answers).sort(byKey)) {
        if (!isAnswered(inp.answers[k].value))
            continue;
        if (!counts(k)) {
            ignored.push(k);
            continue;
        }
        effects.push(...answerEffectsOf(k, inp.questions[k], inp.answers[k].value));
    }
    const rules = [];
    for (const key of Object.keys(inp.rules).sort(byKey)) {
        const r = inp.rules[key];
        if (!r.enabled) {
            rules.push({ rule: key, name: r.name, enabled: false, fired: false, trace: null, effects: [] });
            continue;
        }
        const trace = evalNode(r.when, inp, counts);
        const fired = trace.result
            ? r.then.map((e, i) => ({
                id: `rule:${key}:${i}`,
                type: e.type,
                params: e.params,
                source: { kind: 'rule', rule: key },
            }))
            : [];
        effects.push(...fired);
        rules.push({ rule: key, name: r.name, enabled: true, fired: trace.result, trace, effects: fired });
    }
    return { engine: RULES_ENGINE, as_of: inp.as_of, rules, effects, ignored_answers: ignored.sort(byKey) };
}
function answerEffectsOf(key, q, value) {
    if (!q.options.length)
        return q.effects.map((e, i) => ({
            id: `answer:${key}:${i}`,
            type: e.type,
            params: e.params,
            source: { kind: 'answer', question: key },
        }));
    const picked = new Set((Array.isArray(value) ? value : [value]).map(String));
    return q.options
        .filter((o) => picked.has(o.value))
        .flatMap((o) => o.effects.map((e, i) => ({
        id: `answer:${key}:${o.value}:${i}`,
        type: e.type,
        params: e.params,
        source: { kind: 'answer', question: key, option: o.value },
    })));
}
function evalNode(c, inp, counts) {
    // Every branch is evaluated (no short-circuit) so the trace shows each test.
    if ('all' in c) {
        const children = c.all.map((x) => evalNode(x, inp, counts));
        return { kind: 'all', result: children.every((n) => n.result), children };
    }
    if ('any' in c) {
        const children = c.any.map((x) => evalNode(x, inp, counts));
        return { kind: 'any', result: children.some((n) => n.result), children };
    }
    if ('not' in c) {
        const child = evalNode(c.not, inp, counts);
        return { kind: 'not', result: !child.result, child };
    }
    if ('question' in c) {
        const ans = inp.answers[c.question];
        const node = {
            kind: 'test',
            test: describeTest(c),
            question: c.question,
            result: false,
        };
        let actual;
        let provenance;
        if (ans && isAnswered(ans.value)) {
            node.provenance = ans.provenance;
            if (!counts(c.question))
                node.ignored = 'hidden';
            else if (c.provenance && !c.provenance.includes(ans.provenance))
                node.ignored = 'provenance';
            else {
                actual = ans.value;
                provenance = ans.provenance;
                node.actual = actual;
            }
        }
        node.result = test(actual, provenance, c.op, c.value, inp.as_of);
        return node;
    }
    const actual = factValue(inp.facts, c.fact);
    return {
        kind: 'test',
        test: describeTest(c),
        fact: c.fact,
        ...(actual !== undefined ? { actual } : {}),
        result: test(actual, undefined, c.op, c.value, inp.as_of),
    };
}
function test(actual, provenance, op, expected, asOf) {
    switch (op) {
        case 'verified':
            return isAnswered(actual) && provenance !== undefined && VERIFIED_PROVENANCE.has(provenance);
        case 'expired':
        case 'older_than':
        case 'within_last':
        case 'within_next': {
            const d = dateOf(actual);
            if (!d)
                return false;
            if (op === 'expired')
                return d < asOf;
            const n = expected;
            if (op === 'older_than')
                return d < shiftDate(asOf, n, -1);
            if (op === 'within_last')
                return d >= shiftDate(asOf, n, -1) && d <= asOf;
            return d >= asOf && d <= shiftDate(asOf, n, 1);
        }
        default:
            return compare(actual, op, expected);
    }
}
const ISO = /^\d{4}-\d{2}-\d{2}$/;
/** A date answer, or the end of a date range. */
function dateOf(v) {
    if (typeof v === 'string' && ISO.test(v))
        return v;
    if (v && typeof v === 'object' && 'to' in v && typeof v.to === 'string' && ISO.test(v.to))
        return v.to;
    return null;
}
/** Moves an ISO date by a duration. Months and years keep the day, clamped to the month's last day. */
export function shiftDate(iso, duration, sign) {
    const [, n, unit] = /^(\d+)([dwmy])$/.exec(duration);
    const [y, m, d] = iso.split('-').map(Number);
    const pad = (x, w = 2) => String(x).padStart(w, '0');
    if (unit === 'd' || unit === 'w') {
        const days = Number(n) * (unit === 'w' ? 7 : 1);
        return new Date(Date.UTC(y, m - 1, d) + sign * days * 86_400_000).toISOString().slice(0, 10);
    }
    const total = y * 12 + (m - 1) + sign * Number(n) * (unit === 'y' ? 12 : 1);
    const ny = Math.floor(total / 12);
    const nm = total - ny * 12;
    const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
    return `${pad(ny, 4)}-${pad(nm + 1)}-${pad(Math.min(d, last))}`;
}
/** A fact by name, or by dotted path into nested facts. */
function factValue(facts, path) {
    if (Object.hasOwn(facts, path))
        return facts[path];
    let cur = facts;
    for (const part of path.split('.')) {
        if (!cur || typeof cur !== 'object' || !Object.hasOwn(cur, part))
            return undefined;
        cur = cur[part];
    }
    return cur;
}
const OP_TEXT = {
    eq: '=', ne: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', in: 'in', not_in: 'not in', between: 'between',
    contains: 'contains', answered: 'is answered', not_answered: 'is not answered', expired: 'has passed',
    older_than: 'is more than', within_last: 'is within the last', within_next: 'is within the next',
    verified: 'is verified',
}; // prettier-ignore
export function describeTest(c) {
    const subject = 'question' in c ? c.question : c.fact;
    const v = c.value === undefined ? '' : Array.isArray(c.value) ? c.value.join(', ') : String(c.value);
    const tail = c.op === 'older_than' ? ' ago' : '';
    const from = 'provenance' in c && c.provenance ? ` (from ${c.provenance.join(' or ')})` : '';
    return `${subject} ${OP_TEXT[c.op]}${v ? ` ${v}` : ''}${tail}${from}`;
}
const signature = (e) => canonicalJson({ id: e.id, type: e.type, params: e.params });
/** What changes between two outcomes: the heart of a dry run. A null `before` means nothing held. */
export function diffOutcomes(before, after) {
    const firedBefore = new Set(before?.rules.filter((r) => r.fired).map((r) => r.rule) ?? []);
    const firedAfter = new Set(after.rules.filter((r) => r.fired).map((r) => r.rule));
    const was = new Set(before?.effects.map(signature) ?? []);
    const now = new Set(after.effects.map(signature));
    return {
        started: [...firedAfter].filter((r) => !firedBefore.has(r)).sort(byKey),
        stopped: [...firedBefore].filter((r) => !firedAfter.has(r)).sort(byKey),
        added: after.effects.filter((e) => !was.has(signature(e))),
        removed: (before?.effects ?? []).filter((e) => !now.has(signature(e))),
    };
}
export const isEmptyDiff = (d) => !d.started.length && !d.stopped.length && !d.added.length && !d.removed.length;
//# sourceMappingURL=rules.js.map